// Cross-post group picker — a stack of doc-ui Checkbox rows. The
// primary group is excluded (mutually exclusive with the cross-post
// set); when no other groups are available we render an empty-state
// chip rather than disappearing the whole field.

import type { ReactNode } from 'react';
import { Checkbox } from '@viquard/doc-ui/components';
import type { CalendarGroup } from '../../types.js';
import { useCalendarMessages } from '../../i18n.js';
import { uniqueCalendarGroups } from '../../utils/identity.js';

export interface CrossPostListProps {
  readonly groups: readonly CalendarGroup[];
  readonly excludeId: string | null;
  readonly value: readonly string[];
  readonly onChange: (next: readonly string[]) => void;
}

export function CrossPostList(props: CrossPostListProps): ReactNode {
  const messages = useCalendarMessages();
  const { excludeId, value, onChange } = props;
  const groups = uniqueCalendarGroups(props.groups);
  const options = groups.filter((g) => g.id !== excludeId);
  if (options.length === 0) {
    return (
      <span className="vq-cal__composer-cross-empty">
        {messages.composerCrossPostEmpty}
      </span>
    );
  }
  return (
    <div className="vq-cal__composer-cross">
      {options.map((g) => {
        const on = value.includes(g.id);
        const label = (
          <span className="vq-cal__composer-cross-label">
            <span
              className="vq-cal__composer-cross-dot"
              style={g.color !== undefined ? { background: g.color } : undefined}
              aria-hidden="true"
            />
            <span className="vq-cal__composer-cross-name">{g.name}</span>
          </span>
        );
        return (
          <Checkbox
            key={g.id}
            checked={on}
            onCheckedChange={(checked) => {
              const next = checked
                ? [...value, g.id]
                : value.filter((x) => x !== g.id);
              onChange(next);
            }}
            label={label}
            size="sm"
          />
        );
      })}
    </div>
  );
}
